import { BadgeShelf } from "@/components/BadgeShelf";
import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircleHeart, BookA, Brain, ListChecks, BookHeart, Palette, GraduationCap } from "lucide-react";
import { TeachTab } from "@/components/buddy/teach/TeachTab";
import { useProfile } from "@/context/ProfileContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";
import { BuddyCreator } from "@/components/buddy/BuddyCreator";
import { ThoughtSorter } from "@/components/buddy/ThoughtSorter";
import { AssignmentPlanner } from "@/components/buddy/AssignmentPlanner";
import { BuddyMemory } from "@/components/buddy/BuddyMemory";
import { PERSONALITIES } from "@/lib/buddy";

const YOUNG = ["K", "1", "2", "3", "4", "5"];
const TABS = [["sort", "Sort thoughts", Brain], ["plan", "Assignments", ListChecks], ["memory", "Memory", BookHeart], ["look", "Edit buddy", Palette]];

const BuddyHero = ({ buddy, name }) => {
  const { setTutorOpen } = useProfile();
  const trait = PERSONALITIES.find((p) => p[0] === buddy.personality)?.[1];
  return (
    <section className="grid md:grid-cols-12 gap-8 items-center rise">
      <BuddyAvatar buddy={buddy} className="md:col-span-3 w-40 h-40 md:w-full md:h-auto md:aspect-square !rounded-[2rem] text-6xl shadow-xl" testId="buddy-hero-portrait" />
      <div className="md:col-span-9">
        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">Your learning buddy · {trait}</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C4A3E] mt-3" data-testid="buddy-name">{buddy.name}</h1>
        <p className="mt-4 text-stone-600 max-w-xl">Hi {name}! I'm here to help you sort out your thoughts, plan your assignments, and notice what each subject shows you about who you are.</p>
        <button onClick={() => setTutorOpen(true)} data-testid="buddy-talk-btn"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C4A3E] text-white hover:bg-[#1E352B] transition-colors">
          <MessageCircleHeart className="w-4 h-4" /> Talk with {buddy.name}
        </button>
        <Link to="/words" data-testid="buddy-words-btn" className="mt-6 ml-3 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#2C4A3E] text-[#2C4A3E] hover:bg-[#F1EADB] transition-colors">
          <BookA className="w-4 h-4" /> Practice my words
        </Link>
      </div>
    </section>
  );
};

export default function BuddyPage() {
  const { buddy, profile } = useProfile();
  const young = YOUNG.includes(profile.grade);
  const tabs = young ? [["teach", "Teach my buddy", GraduationCap], ...TABS] : TABS;
  const [tab, setTab] = useState(young ? "teach" : "sort");
  if (buddy === undefined) return <div className="max-w-6xl mx-auto px-6 py-20 text-stone-500">Loading…</div>;
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="buddy-page" data-translate>
      {!buddy ? (
        <>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">Meet your companion</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C4A3E] mt-3 mb-10">Create your learning buddy</h1>
          <BuddyCreator />
        </>
      ) : (
        <>
          <BuddyHero buddy={buddy} name={profile.name} />
          {profile.role === "student" && <BadgeShelf />}
          <Tabs value={tab} onValueChange={setTab} className="mt-14">
            <TabsList className="bg-[#F1EADB] rounded-full p-1 h-auto flex-wrap">
              {tabs.map(([k, label, Icon]) => (
                <TabsTrigger key={k} value={k} data-testid={`buddy-tab-${k}`} className="rounded-full px-4 py-2 data-[state=active]:bg-[#2C4A3E] data-[state=active]:text-white">
                  <Icon className="w-4 h-4 mr-1.5" />{label}
                </TabsTrigger>
              ))}
            </TabsList>
            {young && <TabsContent value="teach" className="mt-8"><TeachTab /></TabsContent>}
            <TabsContent value="sort" className="mt-8"><ThoughtSorter /></TabsContent>
            <TabsContent value="plan" className="mt-8"><AssignmentPlanner /></TabsContent>
            <TabsContent value="memory" className="mt-8"><BuddyMemory /></TabsContent>
            <TabsContent value="look" className="mt-8"><BuddyCreator onDone={() => setTab(young ? "teach" : "sort")} /></TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
